// §10.1 / §6.4 — the clipboard-history panel.
//
// **One component both skins mount**, for the same reason the in-window menu bar is one (D-123):
// §6.1's component list is closed and has no entry for this, and the panel's *behaviour* does not
// differ between skins — only where it sits, which each skin's sheet decides from `data-skin`.
//
// It is the second of §10.1's two shell-routed gestures. The first is a real `paste` on the
// shell's document (D-134); this is the user picking a specific entry off a surface the shell drew,
// which is a gesture an app in an iframe can be the subject of but never the author of.
import { useEffect, useRef } from 'react';
import { COPY } from '@/copy';
import { useClipboardHistory, useKernel, useStoreValue } from '@/kernel/react';

export function ClipboardPanel() {
  const kernel = useKernel();
  const open = useStoreValue(kernel.shellui.store, (state) => state.overlay === 'clipboard');
  const history = useClipboardHistory();
  const panel = useRef<HTMLDivElement | null>(null);

  // Focus the panel when it opens, so Escape and the arrow keys reach it rather than the app
  // underneath — which is still the *focused window*, and must stay so: the pick is delivered
  // there, and taking window focus away to open a panel would change the answer.
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  if (!open) return null;

  const pick = (entryId: string): void => {
    const focusedId = kernel.wm.getState().focusedId;
    const target = focusedId === null ? null : kernel.wm.get(focusedId);
    kernel.shellui.closeOverlay();
    if (!target) return;
    // Two things, in this order: arm the window so its own `readClipboard()` would now work, and
    // push the payload at it so an app that never calls that still receives the paste (§10.1 —
    // "delivers the payload to the focused app").
    kernel.clipboard.armGesture(target.instanceKey, entryId);
    const entry = kernel.clipboard.history().find((item) => item.id === entryId);
    if (entry) kernel.deliverPayload(target.instanceKey, entry.payload);
  };

  return (
    <div
      className="clipboard-panel"
      data-testid="clipboard-history"
      role="dialog"
      aria-label={COPY.clipboard.title}
      tabIndex={-1}
      ref={panel}
      onKeyDown={(event) => {
        if (event.key === 'Escape') kernel.shellui.closeOverlay();
      }}
    >
      <h2 className="clipboard-title">{COPY.clipboard.title}</h2>
      {history.length === 0 ? (
        <p className="clipboard-empty" data-testid="clipboard-empty">
          {COPY.clipboard.empty}
        </p>
      ) : (
        <ul className="clipboard-list" data-testid="clipboard-list">
          {history.map((entry, index) => (
            <li key={entry.id}>
              <button
                type="button"
                className="clipboard-entry"
                data-testid={`clipboard-entry-${index}`}
                data-entry-id={entry.id}
                onClick={() => pick(entry.id)}
              >
                <span className="clipboard-preview">{entry.preview}</span>
                <span className="clipboard-source">{entry.appId}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
